import React, { useState, useEffect } from "react";

const RAILWAY_BASE_URL = "https://plasann-production.up.railway.app";

// File descriptions mapping
const fileDescriptions = {
  "Database.csv": "Main annotated plasmid entries",
  "uniprot_plasmids.tsv": "UniProt metadata for plasmids", 
  "Rfam.cm": "Covariance model for ncRNA detection",
  "plasmidfinder.fasta": "Reference replicons for origin prediction",
  "tncentral_cleaned.fa": "TnCentral mobile element database",
  "transposon.fasta": "Transposon reference sequences",
  "oric.fna": "oriC origin reference sequences",
  "oriT_TNcentral.fasta": "oriT dataset from TnCentral",
  "orit.fna": "oriT origin sequences",
  // Add more as needed
};

export default function DownloadTable() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async (attempt = 1, maxRetries = 3) => {
    try {
      console.log(`Attempting to fetch files (attempt ${attempt}/${maxRetries})`);
      setRetryCount(attempt);
      
      // Add timeout for fetch request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await fetch(`${RAILWAY_BASE_URL}/api/database/list`, {
        signal: controller.signal,
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Successfully fetched file list:', data);
        
        // FILTER: Only show files that we have descriptions for
        const filteredFiles = data.files.filter(file => 
          fileDescriptions.hasOwnProperty(file.name)
        );
        
        // Add descriptions to filtered files
        const filesWithDescriptions = filteredFiles.map(file => ({
          ...file,
          description: fileDescriptions[file.name],
          downloadUrl: `${RAILWAY_BASE_URL}/api/download/${file.name}`
        }));
        
        setFiles(filesWithDescriptions);
        setError(null); // Clear any previous errors
        setLoading(false);
        return; // Success - exit function
        
      } else {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
    } catch (err) {
      console.error(`Error fetching files (attempt ${attempt}):`, err);
      
      // If this isn't the last attempt, retry after a delay
      if (attempt < maxRetries) {
        const delay = attempt * 2000; // Exponential backoff: 2s, 4s, 6s
        console.log(`Retrying in ${delay}ms...`);
        
        setTimeout(() => {
          fetchFiles(attempt + 1, maxRetries);
        }, delay);
        
        return; // Don't fall back to hardcoded list yet
      }
      
      // All retries failed - fall back to hardcoded list
      console.error('All fetch attempts failed, using fallback data');
      setError(`Failed to fetch live data (${attempt} attempts). Using cached file list.`);
      
      // Fallback to hardcoded list if API fails - ONLY the files we want
      setFiles([
        { name: "Database.csv", description: "Main annotated plasmid entries", downloadUrl: `${RAILWAY_BASE_URL}/api/download/Database.csv`, size: null },
        { name: "uniprot_plasmids.tsv", description: "UniProt metadata for plasmids", downloadUrl: `${RAILWAY_BASE_URL}/api/download/uniprot_plasmids.tsv`, size: null },
        { name: "Rfam.cm", description: "Covariance model for ncRNA detection", downloadUrl: `${RAILWAY_BASE_URL}/api/download/Rfam.cm`, size: null },
        { name: "plasmidfinder.fasta", description: "Reference replicons for origin prediction", downloadUrl: `${RAILWAY_BASE_URL}/api/download/plasmidfinder.fasta`, size: null },
        { name: "tncentral_cleaned.fa", description: "TnCentral mobile element database", downloadUrl: `${RAILWAY_BASE_URL}/api/download/tncentral_cleaned.fa`, size: null },
        { name: "transposon.fasta", description: "Transposon reference sequences", downloadUrl: `${RAILWAY_BASE_URL}/api/download/transposon.fasta`, size: null },
        { name: "oric.fna", description: "oriC origin reference sequences", downloadUrl: `${RAILWAY_BASE_URL}/api/download/oric.fna`, size: null },
        { name: "oriT_TNcentral.fasta", description: "oriT dataset from TnCentral", downloadUrl: `${RAILWAY_BASE_URL}/api/download/oriT_TNcentral.fasta`, size: null },
        { name: "orit.fna", description: "oriT origin sequences", downloadUrl: `${RAILWAY_BASE_URL}/api/download/orit.fna`, size: null }
      ]);
      
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) {
      return `${mb.toFixed(1)} MB`;
    } else {
      const kb = bytes / 1024;
      return `${kb.toFixed(1)} KB`;
    }
  };

  // Manual retry function
  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setRetryCount(0);
    fetchFiles();
  };

  if (loading) {
    return (
      <div className="max-h-[500px] overflow-auto border border-gray-300 rounded-md shadow-md">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#354931] border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">
            {retryCount > 1 ? `Retrying... (attempt ${retryCount}/3)` : 'Loading database files...'}
          </p>
          {retryCount > 1 && (
            <p className="text-sm text-gray-500 mt-2">
              Server may be waking up from sleep mode...
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-h-[500px] overflow-auto border border-gray-300 rounded-md shadow-md">
      {error && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-2 flex justify-between items-center">
          <div>
            <p className="text-sm font-medium">⚠️ {error}</p>
            <p className="text-xs mt-1">Downloads still work, but file sizes may not be accurate.</p>
          </div>
          <button 
            onClick={handleRetry}
            className="ml-4 px-3 py-1 bg-yellow-600 hover:bg-yellow-700 text-white text-xs rounded transition-colors"
          >
            Retry
          </button>
        </div>
      )}
      
      <table className="min-w-full text-left text-xl text-gray-800">
        <thead className="text-white text-xl sticky top-0 z-10" style={{ background: 'linear-gradient(135deg, #354931 0%, #5d8156 50%, #86bb7c 100%)'}}>
          <tr>
            <th className="p-4 w-1/4">📁 File Name</th>
            <th className="p-4 w-1/2">📝 Description</th>
            <th className="p-4 w-1/6 text-center">📏 Size</th>
            <th className="p-4 w-1/6 text-center">⬇️ Download</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file, idx) => (
            <tr key={idx} className="even:bg-gray-50 hover:bg-blue-50 transition-colors">
              <td className="p-4 font-medium font-mono text-lg">{file.name}</td>
              <td className="p-4">{file.description}</td>
              <td className="p-4 text-center text-gray-600 text-lg">
                {file.size ? formatFileSize(file.size) : (
                  <span className="text-gray-400" title="Size unavailable due to server status">N/A</span>
                )}
              </td>
              <td className="p-4 text-center">
                <a
                  href={file.downloadUrl}
                  download={file.name}
                  className="inline-block px-3 py-1 bg-[#354931] hover:bg-[#2B3D27] text-white rounded transition"
                  title={`Download ${file.name}`}
                >
                  Download
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      
      <div className="p-4 bg-gray-50 border-t text-sm text-gray-600">
        <p>💡 <strong>Direct from server:</strong> Files are served directly from our Railway backend for faster downloads.</p>
        {error && (
          <p className="text-xs text-gray-500 mt-1">
            💤 <strong>Serverless mode:</strong> If the server was sleeping, file sizes may take a moment to load.
          </p>
        )}
      </div>
    </div>
  );
}
